import React from 'react';
import { UseFormMethods } from 'react-hook-form';
import { AppOption, LinkFields, TrackEvent, RequestStatus } from '../../types';
import { Checkbox } from '../Common/Checkbox';
import { Field } from './Field';
import { Select } from './Select';
import { Input } from './Input';

interface FieldAppTrackerProps {
  form: UseFormMethods<LinkFields>;
  appName: keyof LinkFields;
  trackerName: keyof LinkFields;
  manualTrackerName: keyof LinkFields;
  appsOptions: AppOption[];
  onAppChange: (token: string) => void;
  onTrackEvent: TrackEvent;
  labelPrefix?: string;
  appOption?: AppOption;
}

export const FieldAppTracker: React.FC<FieldAppTrackerProps> = ({
  form,
  appName,
  trackerName,
  manualTrackerName,
  appsOptions,
  onAppChange,
  onTrackEvent,
  labelPrefix = '',
  appOption,
}) => {
  const appToken = form.watch(appName) as string;
  const manualTracker = form.watch(manualTrackerName);
  const trackerOptions = appOption?.trackerOptions || [];

  const handleAppChange = (token: string) => {
    onAppChange(token);
    form.clearErrors(trackerName);
  };

  const handleManualChange = (checked: boolean) => {
    if (checked) onTrackEvent('checkbox_manual');
    form.clearErrors(trackerName);
  };

  return (
    <div className="form-row form-row-group">
      <Field
        form={form}
        name={appName}
        label={`${labelPrefix}App`}
        component={<Select placeholder="--- Select an App ---" options={appsOptions} onChange={handleAppChange} />}
      />

      <Field
        form={form}
        name={trackerName}
        label={`${labelPrefix}Tracker`}
        component={
          manualTracker ? (
            <Input autoFocus />
          ) : (
            <Select
              placeholder={getTrackerPlaceholder({
                isLoading: appOption?.detailsStatus === RequestStatus.fetching,
                appToken,
                hasTrackers: trackerOptions.length > 0,
              })}
              options={trackerOptions}
            />
          )
        }
      />

      <div className="form-row-checkbox">
        <Checkbox ref={form.register} name={manualTrackerName} label="manual" onChange={handleManualChange} />
      </div>
    </div>
  );
};

interface GetTrackerPlaceholderProps {
  isLoading: boolean;
  hasTrackers: boolean;
  appToken?: string;
}

const getTrackerPlaceholder = ({ isLoading, appToken, hasTrackers }: GetTrackerPlaceholderProps) => {
  if (isLoading) return '--- Loading ---';
  if (!appToken) return '--- You must select an app first ---';
  return hasTrackers ? '--- Select a Tracker ---' : 'No trackers available';
};
